import { useEffect, useMemo, useRef, useState } from 'react';
import { useManagedTimeout } from '@/shared/hooks';

/** An estimate, since no source gives a figure: meant to cover finishing one cell and starting the next. */
export const IDLE_CHECK_MS = 2000;

/**
 * Why the check may not run now.
 *
 * `editor`: a grid cell or column rename is open, and the user may still be typing.
 * `popover`: the details popover is open, and a new report would move its list under the pointer.
 * `lists`: shared lists are still loading, and a check would judge bindings it cannot see.
 */
export type HoldReason = 'editor' | 'popover' | 'lists';

export interface IdleCheckHandle {
  /** Restarts the wait. The check runs once the user has been idle for the whole of it. */
  schedule: () => void;
  /**
   * Runs the check after the next render, skipping the wait.
   *
   * After the render rather than now: the caller has usually just edited, and
   * the check has to read the schema that edit produces, including the shared
   * list values derived from it. Under a hold it falls due instead.
   */
  runSoon: () => void;
  /** Drops a pending timed check, a check that fell due under a hold, and a `runSoon` not yet run. */
  cancel: () => void;
  /**
   * Turns a hold on or off. A check that falls due under a hold runs when the
   * last hold is released: after the full wait when that was an editor, since
   * the user may type again, and straight away otherwise.
   */
  setHeld: (reason: HoldReason, on: boolean) => void;
}

export function useIdleCheck(
  run: () => void,
  {
    listsPending = false,
    delayMs = IDLE_CHECK_MS,
  }: { listsPending?: boolean; delayMs?: number } = {},
): IdleCheckHandle {
  const timeout = useManagedTimeout();
  const runRef = useRef(run);
  runRef.current = run;
  const heldRef = useRef(new Set<HoldReason>());
  const dueRef = useRef(false);
  const requestedRef = useRef(false);
  const [immediateRequest, setImmediateRequest] = useState(0);

  const handle = useMemo<IdleCheckHandle>(() => {
    const fire = () => {
      if (heldRef.current.size > 0) {
        dueRef.current = true;
        return;
      }
      dueRef.current = false;
      runRef.current();
    };
    const schedule = () => timeout.start(fire, delayMs);
    const runSoon = () => {
      timeout.clear();
      dueRef.current = false;
      requestedRef.current = true;
      setImmediateRequest((n) => n + 1);
    };
    return {
      schedule,
      runSoon,
      cancel: () => {
        timeout.clear();
        dueRef.current = false;
        requestedRef.current = false;
      },
      setHeld: (reason, on) => {
        const held = heldRef.current;
        if (on) {
          held.add(reason);
          return;
        }
        if (!held.delete(reason) || held.size > 0 || !dueRef.current) return;
        dueRef.current = false;
        if (reason === 'editor') schedule();
        else runSoon();
      },
    };
  }, [timeout, delayMs]);

  // Before the request effect: an edit that makes lists pending and asks for a
  // check in the same commit must find the lists hold already on.
  useEffect(() => {
    handle.setHeld('lists', listsPending);
  }, [handle, listsPending]);

  useEffect(() => {
    if (!requestedRef.current) return;
    requestedRef.current = false;
    if (heldRef.current.size > 0) {
      dueRef.current = true;
      return;
    }
    runRef.current();
  }, [immediateRequest]);

  return handle;
}
